import React, { useEffect, useRef, useState } from "react"
import Add from '../../img/add.svg'
import useEstimateStore from "../../stores/estimate"
import { addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore"
import { db } from "../../config/firebase"
import axios from "axios"
import { BASEURL } from '../../config'
import useAccountStore from "../../stores/account"
import DateTimePicker from "../../components/DateTimeInputComponent"

const Chatting = () => {
    const {accessToken} = useAccountStore()
    const {curEsti} = useEstimateStore()
    const [chatRoomName, setChatRoomName] = useState("")
    const [messages, setMessages] = useState<any[]>([])

    const formatDateToDateTimeString = (date:any) => {
        if (!date) return "-"
      
        const d = new Date(date)
        const year = d.getFullYear()
        const month = (`0${d.getMonth() + 1}`).slice(-2) // 월은 0부터 시작하므로 1을 더해줍니다.
        const day = (`0${d.getDate()}`).slice(-2)
        const hours = (`0${d.getHours()}`).slice(-2)
        const minutes = (`0${d.getMinutes()}`).slice(-2)
        return `${year}.${month}.${day} ${hours}:${minutes}`
    }

    
    const createChatID = (createdAt:Date, estimateID:number) => {
        const year = createdAt.getFullYear().toString().slice(-2) // 년도의 마지막 두 자리
        const month = (createdAt.getMonth() + 1).toString().padStart(2, '0') // 월
        const date = createdAt.getDate().toString().padStart(2, '0') // 일
        const hour = createdAt.getHours().toString().padStart(2, '0') // 시
        const minute = createdAt.getMinutes().toString().padStart(2, '0') // 분
        const dateStr = `${year}${month}${date}${hour}${minute}`
        return `CHAT_${dateStr}_${estimateID}`
    }

    // curEsti에 따른 채팅방 이름 지정
    useEffect(()=>{
        if(curEsti === null) return
        if(curEsti.proposalList !=null) setChatRoomName(`${createChatID(new Date(curEsti.createdAt), curEsti.proposalList?.estimateID)}_${curEsti.userID}_${curEsti.proposalList?.hospitalID}`)
        else setChatRoomName(`${createChatID(new Date(curEsti.createdAt), curEsti.id)}_${curEsti.userID}_${curEsti.hospitalID}`)
    },[curEsti])

    useEffect(()=>{
        if(!chatRoomName) return

        // `onSnapshot` 호출로부터 반환된 unsubscribe 함수를 저장하기 위한 변수
        let unsubscribe:any
        
        const setChatRoomRef = async () => {
            // 기존의 리스너를 해제
            if (unsubscribe) { unsubscribe()}
            const messagesQuery = query(collection(db, `estiChat/${chatRoomName}/messages`), orderBy("createAt"))
            // 새로운 chatRoomName에 대한 리스너 등록
            unsubscribe = onSnapshot(messagesQuery, (snapshot): void => {
                // 모든 문서 변경사항을 반영하여 메시지 목록 업데이트
                const updatedMessages = snapshot.docs.map(doc => ({id: doc.id, ...doc.data(), createAt: doc.data().createAt.toDate().toLocaleString()}))
                setMessages(updatedMessages)
            })
        }

        setChatRoomRef()

        // 컴포넌트가 언마운트되거나 chatRoomName이 변경될 때 리스너 해제
        return () => {if (unsubscribe) { unsubscribe() }}
    },[chatRoomName])

    interface Message {
        id: string
        content: string
        sender: string
        createAt: string // 실제 타입에 맞게 조정이 필요할 수 있습니다.
        userID?: string
        type: string // type : message(content + image, only image, only content), reservation(proposal, accept, decline, confirm)
        option?: any
    }
    
    interface MessageBoxProps { message: Message }

    const MessageBox:React.FC<MessageBoxProps> = ({message}) => {
        const isSender = message.sender === "App"

        const reservationThis = async()=> {
            await axios.put(`${BASEURL}/esti/reserve`, { estimate_id: curEsti?.id }, { headers: {Authorization: `Bearer ${accessToken}`, }})
            .then(response => response.data)
            .then(async(obj) => {
                if(obj.status === "success") { 
                    alert("예약확정 되었습니다.")
                    confirmThis()
                }
            }).catch(error => {
                alert("예약확정에 실패했습니다.")

                if(error.response) {
                    // temp error handling
                    if(error.response.status === 401) console.log("토큰이 만료되었습니다.")
                    if(error.response.status === 403) console.log("토큰이 유효하지 않습니다.")
                    if(error.response.status === 404) console.log("사용자 정보를 찾을 수 없습니다.")
                }
                else { console.log(error) }
            })
        }

        const confirmThis = async() => {
            // 예약 수락
            await updateDoc(doc(db, `estiChat/${chatRoomName}/messages/${message.id}`), {
                content: "예약이 확정되었습니다.",
                sender: "Hospital",
                type: "confirm",
                option: {
                    date: message.option?.date,
                    content: "예약이 확정되었습니다."
                }
            })
        }
        // delete proposal message
        const deleteThis = async(id:any)=> { await deleteDoc(doc(db, `estiChat/${chatRoomName}/messages/${id}`)) }
        // cancel proposal message -> type ( proposal -> cancel )
        const cancelThis = async()=> {
            if(!message.option?.date) return alert("예약 시간이 없습니다.")
            if(message.option?.date < new Date()) return alert("이미 지난 시간입니다.")
            let reason = "" // prompt("취소 사유를 입력해주세요.") : prompt 필수
            if(!reason) reason = "병원측 취소"

            await updateDoc(doc(db, `estiChat/${chatRoomName}/messages/${message.id}`), {
                content: "병원이 예약을 취소하였습니다.",
                sender: "Hospital",
                type: "cancel",
                option: {
                    date: message.option?.date,
                    content: reason ?? "병원측 취소"
                }
            })
        }
        
        // message type에 따른 다른 UI를 렌더링
        if(message.type === "proposal") {
            return (
                <div style={{width:'200px', height:'auto', padding:'6px 3px', borderRadius:'10px', background:'#FEFEFE', border:'1px solid #EEEEEE', margin:'5px auto'}}>
                    <div style={{fontSize:'12px', textAlign:'left', marginLeft:'3px', color:'#000', fontWeight:'500'}}> 예약하시겠습니까? </div>
                    <hr style={{backgroundColor:'#D6D6D6', width : '190px', margin:'4px auto', height:'1px', border:'0'}}/>
                    <div style={{display:'flex', fontSize:'14px', justifyContent:'space-between', margin:'5px'}}>
                        <p style={{color:'#7A7A7A'}}>일시</p>
                        <p>{formatDateToDateTimeString(message.option?.date)}</p></div>
                    <div style={{marginTop:'15px', marginBottom:'5px'}}>
                        <button 
                            style={{width:'60px', height:'24px', padding:'6px 3px',
                                    fontSize:'12px', borderRadius:'5px', background:'#51D7DF', color:'#FFFFFF'}}
                            onClick={cancelThis}>제안취소</button>
                    </div>
                </div>
            )
        }
        if(message.type === "cancel") {
            return (
                <div style={{width:'200px', height:'auto', padding:'6px 3px', borderRadius:'10px', background:'#FEFEFE', border:'1px solid #EEEEEE', margin:'5px auto'}}>
                    <div style={{fontSize:'12px', textAlign:'left', marginLeft:'3px', color:'#000', fontWeight:'600'}}> 제안을 취소하였습니다. </div>
                    <hr style={{backgroundColor:'#D6D6D6', width : '190px', margin:'4px auto', height:'1px', border:'0'}}/>
                    <div style={{display:'flex', fontSize:'14px', justifyContent:'space-between', margin:'5px'}}>
                        <p style={{color:'#7A7A7A'}}>일시</p>
                        <p>{formatDateToDateTimeString(message.option?.date)}</p></div>
                    <div style={{display:'flex', fontSize:'14px', justifyContent:'space-between', margin:'5px'}}>
                        <p style={{color:'#7A7A7A'}}>사유</p>
                        <p style={{width:'120px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{message.option?.content}</p></div>
                </div>
            )
        }
        if(message.type === "accept") {
            return (
                <div style={{width:'200px', height:'auto', padding:'6px 3px', borderRadius:'10px', background:'#FEFEFE', border:'1px solid #EEEEEE', margin:'5px auto'}}>
                    <div style={{fontSize:'12px', textAlign:'left', marginLeft:'3px', color:'#000', fontWeight:'600'}}> 예약을 확정하시겠습니까? </div>
                    <hr style={{backgroundColor:'#D6D6D6', width : '190px', margin:'4px auto', height:'1px', border:'0'}}/>
                    <div>{formatDateToDateTimeString(message.option?.date)}</div>
                    <div style={{marginTop:'15px', marginBottom:'5px'}} >
                        <button style={{width:'60px', height:'24px', padding:'6px 3px',
                            fontSize:'12px', borderRadius:'5px', background:'#51D7DF', color:'#FFFFFF', cursor:"pointer", margin:' 0 5px'}} onClick={reservationThis}>예약확정</button>
                        <button style={{width:'60px', height:'24px', padding:'6px 3px',
                            fontSize:'12px', borderRadius:'5px', background:'#E5E5E5', color:'#000', cursor:"pointer", margin:' 0 5px'}} onClick={cancelThis}>예약취소</button>
                    </div>
                </div>
            )
        }

        if(message.type === "decline") {
            return (
                <div style={{width:'200px', height:'auto', padding:'6px 3px', borderRadius:'10px', background:'#FEFEFE', border:'1px solid #EEEEEE', margin:'5px auto'}}>
                    <div style={{fontSize:'12px', textAlign:'left', marginLeft:'3px', color:'#000', fontWeight:'600'}}> 제안에 거절하였습니다. </div>
                    <hr style={{backgroundColor:'#D6D6D6', width : '190px', margin:'4px auto', height:'1px', border:'0'}}/>
                    <div style={{display:'flex', fontSize:'14px', justifyContent:'space-between', margin:'5px'}}>
                        <p style={{color:'#7A7A7A'}}>일시</p>
                        <p>{formatDateToDateTimeString(message.option?.date)}</p></div>
                    <div style={{display:'flex', fontSize:'14px', justifyContent:'space-between', margin:'5px'}}>
                        <p style={{color:'#7A7A7A'}}>사유</p>
                        <p style={{width:'120px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{message.option?.content ?? "-"}</p></div>
                </div>
            )
        }
        if(message.type === "confirm") {
            return (
                <div style={{width:'200px', height:'auto', padding:'6px 3px', borderRadius:'10px', background:'#FEFEFE', border:'1px solid #EEEEEE', margin:'5px auto'}}>
                    <div style={{fontSize:'12px', textAlign:'left', marginLeft:'3px', color:'#000', fontWeight:'600'}}> 예약에 성공하였습니다. </div>
                    <hr style={{backgroundColor:'#D6D6D6', width : '190px', margin:'4px auto', height:'1px', border:'0'}}/>
                    <div style={{display:'flex', fontSize:'14px', justifyContent:'space-between', margin:'5px'}}>
                        <p style={{color:'#7A7A7A'}}>일시</p>
                        <p>{formatDateToDateTimeString(message.option?.date)}</p></div>    
                </div>
            )
        }
        // 기본 메세지 UI
        return (
            <div className={`${isSender ? "sender " : "receiver "} msg_box`}>
                {isSender && <span className="name">{message?.userID ?? "user"}</span>}
                <div className="msg">
                    {message.content != "" && <div className="message"> {message.content} </div>}
                    <div className="images">
                        { message.option?.contents?.map((content:any, index:number) => { return <img src={content} key={index} 
                            style={ message.option?.contents?.length == 1 ? {width:'200px', height:'100px'} : {width:'44px', height:'44px'}}/> })}
                    </div>    
                </div>
                <div className="msg_date">{message.createAt}</div>
            </div>
        )
    }

    const MessageLog = () => {
        // 자동으로 맨아래로 스크롤
        const messagesEndRef = useRef<HTMLDivElement>(null)
        useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

        return (
            <div className="con">
                { messages.map((msg) => { return ( <MessageBox key={msg.id} message={msg}/> ) }) }
                <div ref={messagesEndRef} />
            </div>
        )
    }
    
    const WriteMsgContainer = () => {
        const [message, setMessage] = useState("")
        const [reservationTime, setReservationTime] = useState(new Date())
        const [contents, setContents] = useState<any[]>([]) // image, video, file 등의 파일을 저장하는 state
        const [showAttach, setShowAttach] = useState(false)
        const [showReservationTime, setShowReservationTime] = useState(false)

        const handleShowAttach = () => { setShowAttach(!showAttach) }
        const handleShowReservationTime = async() => { setShowReservationTime(!showReservationTime) }
        
         // 보낼 수 있는 메시지 타입 : message, image(only 이미지), reservation(proposal, accept, decline, confirm)
         // reservation
         // proposal : 예약 제안 (병원이 환자에게), -- 예약 시간 (2023.08.20 16:00), -- 예약 내용 (예약을 하시겠습니까?) -- 제안 취소 (type change cancel)
         // accept : 예약 수락 (환자가 병원에게), 병원은 예약 제안 수정 불가능, 예약 제안 취소는 할 수 있음
         // decline : 예약 거절 (환자가 병원에게), 환자가 예약 제안을 거절하였습니다.
         // cancel : 예약 취소, -- 취소 사유 (병원이 예약을 취소하였습니다.)
         // confirm : 예약 확정완료 (병원이 환자에게)

        const sendReserveProposal = async() => {
            // 예약 제안
            await addDoc(collection(db, `estiChat/${chatRoomName}/messages`), {
                createAt: new Date(),
                content: "예약을 하시겠습니까?",
                sender: "Hospital",
                type: "proposal",
                option: {
                    date: reservationTime.toString(),
                    content: "예약을 하시겠습니까?"
                }
            })
            handleShowAttach()
        }
        // send message : image, message
        const sendMessage = async() => {
            if(message === "" && contents.length == 0) return

            const messageData = {
                createAt: new Date(),
                content: message,
                sender: "Hospital",
                type: "message",
                option: contents.length > 0 ? { contents: contents } : {}
            }

            await addDoc(collection(db, `estiChat/${chatRoomName}/messages`), messageData)
        }

        const ReserveTimeInputPopup = ()=> {
            return (
                <div style={{width:'200px', height:'100%', background:"#51D7DF33", borderRadius:"5px",
                    padding:"5px 3px", margin:"0 auto", marginTop:"3px", alignContent:"center"}}>
                    <div style={{marginLeft:'25px'}}>
                        <DateTimePicker selectedDate={reservationTime} setSelectedDate={setReservationTime}/>
                    </div>
                    <div style={{marginTop:"5px"}}>
                        <button onClick={()=>{sendReserveProposal()}}
                        style={{ background:"#51D7DF", borderRadius:"5px",
                        padding:"5px 3px", color:'white', fontSize:'12px'}}><p style={{fontSize:'12px'}}>예약시간 제안</p></button>
                    </div>
                </div>
            )
        }

        const uploadImage = async(data: any, dataName:string) => {
            // dataUrl -> Blob -> File로 변환
            const dataUrlToBlob = (dataUrl: string) => {
                const arr = dataUrl.split(',')
                const mime = arr[0].match(/:(.*?);/)![1]
                const bstr = atob(arr[1])
                let n = bstr.length
                let u8arr = new Uint8Array(n)
                while(n--) { u8arr[n] = bstr.charCodeAt(n) }
                return new Blob([u8arr], { type: mime })
            }
            
            const blobToFile = (theBlob: Blob, fileName: string) => {
                const file = new File([theBlob], fileName, { type: theBlob.type })
                return file
            }
    
            // dataUrl -> Blob -> File로 변환끝
    
            // upload file to server
            let formData = new FormData()
            const blob = dataUrlToBlob(data)
            const file = blobToFile(blob, `${dataName}.png`)
            formData.append("file", file)
            formData.append("id", curEsti?.id.toString()?? "error")
            formData.append("type", "chat")

            const url =  await axios.put(`${BASEURL}/auth/upload/file`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${accessToken}`,
                }
            }).then(response => {
                if (response.data.status === "success") {
                    return response.data.data
                } else { return '' }
            }).catch(error => {
                alert(`파일 업로드 실패: ${error.message}`)
                return ''
            })
            return url
        }

        const handleImageChange = (e:any) => {
            const reader = new FileReader()
            const file = e.target.files[0]

            reader.onloadend = async() => { 
                const newContents = [...contents]
                const url = await uploadImage(reader.result?.toString()??'', new Date().getTime().toString())
                if(!url) return

                newContents.push(url)
                setContents(newContents)
            }
            if (file) { reader.readAsDataURL(file) }
            handleShowAttach()
        }

        const removeImage = (index:number) => { setContents(contents.filter((_, i) => i !== index)) }
        
        const AttachList = () => {
            return (
                <div className="attach_list" style={{ display: showAttach ? 'block' : 'none' }}>
                    <ul>
                        <li style={contents.length >= 3 ? {opacity:'0.3', pointerEvents:'none'} : {}}>
                            <label htmlFor="img_attach">
                                <div className="icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M15.8337 1.66663H4.16699C3.50395 1.66663 2.86807 1.93002 2.39923 2.39886C1.93038 2.8677 1.66699 3.50358 1.66699 4.16663V15.8333C1.66699 16.4963 1.93038 17.1322 2.39923 17.6011C2.86807 18.0699 3.50395 18.3333 4.16699 18.3333H15.8337C15.9707 18.3314 16.1074 18.3174 16.242 18.2916L16.492 18.2333H16.5503H16.592L16.9003 18.1166L17.0087 18.0583C17.092 18.0083 17.1837 17.9666 17.267 17.9083C17.3783 17.8264 17.484 17.7373 17.5837 17.6416L17.642 17.5666C17.7238 17.4837 17.799 17.3946 17.867 17.3L17.942 17.1916C18.0002 17.0988 18.0504 17.0013 18.092 16.9C18.1149 16.86 18.1344 16.8182 18.1503 16.775C18.192 16.675 18.217 16.5666 18.2503 16.4583V16.3333C18.2976 16.1705 18.3255 16.0026 18.3337 15.8333V4.16663C18.3337 3.50358 18.0703 2.8677 17.6014 2.39886C17.1326 1.93002 16.4967 1.66663 15.8337 1.66663ZM4.16699 16.6666C3.94598 16.6666 3.73402 16.5788 3.57774 16.4225C3.42146 16.2663 3.33366 16.0543 3.33366 15.8333V12.2416L6.07533 9.49163C6.15279 9.41352 6.24496 9.35152 6.34651 9.30922C6.44806 9.26691 6.55698 9.24513 6.66699 9.24513C6.777 9.24513 6.88592 9.26691 6.98747 9.30922C7.08902 9.35152 7.18119 9.41352 7.25866 9.49163L14.4253 16.6666H4.16699ZM16.667 15.8333C16.6662 15.936 16.6464 16.0377 16.6087 16.1333C16.5896 16.1739 16.5673 16.2129 16.542 16.25C16.5197 16.2852 16.4946 16.3187 16.467 16.35L12.0087 11.8916L12.742 11.1583C12.8195 11.0802 12.9116 11.0182 13.0132 10.9759C13.1147 10.9336 13.2236 10.9118 13.3337 10.9118C13.4437 10.9118 13.5526 10.9336 13.6541 10.9759C13.7557 11.0182 13.8479 11.0802 13.9253 11.1583L16.667 13.9083V15.8333ZM16.667 11.55L15.1003 9.99996C14.6234 9.54745 13.9911 9.29519 13.3337 9.29519C12.6762 9.29519 12.0439 9.54745 11.567 9.99996L10.8337 10.7333L8.43366 8.33329C7.95676 7.88079 7.32441 7.62852 6.66699 7.62852C6.00958 7.62852 5.37722 7.88079 4.90033 8.33329L3.33366 9.88329V4.16663C3.33366 3.94561 3.42146 3.73365 3.57774 3.57737C3.73402 3.42109 3.94598 3.33329 4.16699 3.33329H15.8337C16.0547 3.33329 16.2666 3.42109 16.4229 3.57737C16.5792 3.73365 16.667 3.94561 16.667 4.16663V11.55ZM11.2503 4.99996C11.0031 4.99996 10.7614 5.07327 10.5559 5.21062C10.3503 5.34797 10.1901 5.5432 10.0955 5.77161C10.0009 6.00001 9.97611 6.25135 10.0243 6.49382C10.0726 6.7363 10.1916 6.95903 10.3664 7.13384C10.5413 7.30866 10.764 7.42771 11.0065 7.47594C11.2489 7.52417 11.5003 7.49942 11.7287 7.40481C11.9571 7.3102 12.1523 7.14998 12.2897 6.94442C12.427 6.73886 12.5003 6.49719 12.5003 6.24996C12.5003 5.91844 12.3686 5.6005 12.1342 5.36608C11.8998 5.13166 11.5818 4.99996 11.2503 4.99996Z" fill="white"/>
                                </svg>
                                </div>
                                <span>이미지</span>
                            </label>
                            <input id="img_attach" type="file" accept="image/gif,image/jpeg,image/png" onChange={handleImageChange}/>
                        </li>
                        <li style={{opacity:'0.3', pointerEvents:'none'}}>
                            <label htmlFor="video_attach">
                                <div className="icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                                    <path d="M14.6663 9.41421L10.083 6.76504C9.80445 6.60421 9.48847 6.5195 9.16681 6.51942C8.84516 6.51934 8.52914 6.60388 8.25049 6.76457C7.97184 6.92526 7.74038 7.15643 7.57934 7.43487C7.41829 7.71331 7.33334 8.02922 7.33301 8.35087V13.6492C7.33334 13.9707 7.41821 14.2865 7.5791 14.5648C7.73999 14.8432 7.97125 15.0743 8.24968 15.235C8.52838 15.3959 8.84453 15.4807 9.16635 15.4807C9.48816 15.4807 9.80431 15.3959 10.083 15.235L14.6663 12.5859C14.9442 12.4247 15.1748 12.1934 15.3351 11.9151C15.4954 11.6368 15.5798 11.3212 15.5798 11C15.5798 10.6789 15.4954 10.3633 15.3351 10.085C15.1748 9.80668 14.9442 9.57537 14.6663 9.41421ZM13.7497 11L9.16635 13.6492V8.35087L13.7497 11ZM10.9997 1.83337C9.18668 1.83337 7.4144 2.37099 5.90695 3.37824C4.3995 4.38548 3.22459 5.81712 2.53078 7.49211C1.83698 9.1671 1.65545 11.0102 2.00915 12.7884C2.36284 14.5665 3.23589 16.1999 4.51787 17.4819C5.79985 18.7638 7.43319 19.6369 9.21135 19.9906C10.9895 20.3443 12.8326 20.1627 14.5076 19.4689C16.1826 18.7751 17.6142 17.6002 18.6215 16.0928C19.6287 14.5853 20.1663 12.813 20.1663 11C20.1663 9.79626 19.9292 8.60426 19.4686 7.49211C19.0079 6.37996 18.3327 5.36943 17.4815 4.51823C16.6303 3.66702 15.6198 2.99181 14.5076 2.53114C13.3955 2.07048 12.2035 1.83337 10.9997 1.83337ZM10.9997 18.3334C9.54928 18.3334 8.13146 17.9033 6.9255 17.0975C5.71954 16.2917 4.7796 15.1464 4.22456 13.8064C3.66952 12.4664 3.5243 10.9919 3.80725 9.56938C4.09021 8.14685 4.78864 6.84018 5.81423 5.81459C6.83981 4.78901 8.14649 4.09057 9.56902 3.80762C10.9915 3.52466 12.466 3.66988 13.806 4.22492C15.146 4.77997 16.2913 5.7199 17.0971 6.92586C17.9029 8.13182 18.333 9.54964 18.333 11C18.333 12.945 17.5604 14.8102 16.1851 16.1855C14.8099 17.5608 12.9446 18.3334 10.9997 18.3334Z" fill="white"/>
                                </svg>
                                </div>
                                <span>동영상</span>
                            </label>
                            <input id="video_attach" type="file" accept="video/*"/>
                        </li>
                        <li style={{opacity:'0.3', pointerEvents:'none'}}>
                            <label htmlFor="file_attach">
                                <div className="icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M9 10H10C10.2652 10 10.5196 9.89464 10.7071 9.70711C10.8946 9.51957 11 9.26522 11 9C11 8.73478 10.8946 8.48043 10.7071 8.29289C10.5196 8.10536 10.2652 8 10 8H9C8.73478 8 8.48043 8.10536 8.29289 8.29289C8.10536 8.48043 8 8.73478 8 9C8 9.26522 8.10536 9.51957 8.29289 9.70711C8.48043 9.89464 8.73478 10 9 10ZM9 12C8.73478 12 8.48043 12.1054 8.29289 12.2929C8.10536 12.4804 8 12.7348 8 13C8 13.2652 8.10536 13.5196 8.29289 13.7071C8.48043 13.8946 8.73478 14 9 14H15C15.2652 14 15.5196 13.8946 15.7071 13.7071C15.8946 13.5196 16 13.2652 16 13C16 12.7348 15.8946 12.4804 15.7071 12.2929C15.5196 12.1054 15.2652 12 15 12H9ZM20 8.94C19.9896 8.84813 19.9695 8.75763 19.94 8.67V8.58C19.8919 8.47718 19.8278 8.38267 19.75 8.3L13.75 2.3C13.6673 2.22222 13.5728 2.15808 13.47 2.11C13.4402 2.10576 13.4099 2.10576 13.38 2.11C13.2784 2.05174 13.1662 2.01434 13.05 2H7C6.20435 2 5.44129 2.31607 4.87868 2.87868C4.31607 3.44129 4 4.20435 4 5V19C4 19.7956 4.31607 20.5587 4.87868 21.1213C5.44129 21.6839 6.20435 22 7 22H17C17.7956 22 18.5587 21.6839 19.1213 21.1213C19.6839 20.5587 20 19.7956 20 19V9C20 9 20 9 20 8.94ZM14 5.41L16.59 8H15C14.7348 8 14.4804 7.89464 14.2929 7.70711C14.1054 7.51957 14 7.26522 14 7V5.41ZM18 19C18 19.2652 17.8946 19.5196 17.7071 19.7071C17.5196 19.8946 17.2652 20 17 20H7C6.73478 20 6.48043 19.8946 6.29289 19.7071C6.10536 19.5196 6 19.2652 6 19V5C6 4.73478 6.10536 4.48043 6.29289 4.29289C6.48043 4.10536 6.73478 4 7 4H12V7C12 7.79565 12.3161 8.55871 12.8787 9.12132C13.4413 9.68393 14.2044 10 15 10H18V19ZM15 16H9C8.73478 16 8.48043 16.1054 8.29289 16.2929C8.10536 16.4804 8 16.7348 8 17C8 17.2652 8.10536 17.5196 8.29289 17.7071C8.48043 17.8946 8.73478 18 9 18H15C15.2652 18 15.5196 17.8946 15.7071 17.7071C15.8946 17.5196 16 17.2652 16 17C16 16.7348 15.8946 16.4804 15.7071 16.2929C15.5196 16.1054 15.2652 16 15 16Z" fill="white"/>
                                </svg>
                                </div>
                                <span>파일</span>
                            </label>
                            <input id="file_attach" type="file"/>
                        </li>
                        <li style={curEsti?.status !== "presented" ? { opacity: '0.3', pointerEvents: 'none' } : undefined}>
                            <label htmlFor="schedule_attach" onClick={handleShowReservationTime}>
                                <div className="icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10.0003 11.6666C10.1651 11.6666 10.3263 11.6178 10.4633 11.5262C10.6003 11.4346 10.7072 11.3045 10.7702 11.1522C10.8333 10.9999 10.8498 10.8324 10.8176 10.6707C10.7855 10.5091 10.7061 10.3606 10.5896 10.244C10.473 10.1275 10.3246 10.0481 10.1629 10.016C10.0013 9.98382 9.83369 10.0003 9.68142 10.0634C9.52915 10.1265 9.399 10.2333 9.30743 10.3703C9.21587 10.5074 9.16699 10.6685 9.16699 10.8333C9.16699 11.0543 9.25479 11.2663 9.41107 11.4225C9.56735 11.5788 9.77931 11.6666 10.0003 11.6666ZM14.167 11.6666C14.3318 11.6666 14.4929 11.6178 14.63 11.5262C14.767 11.4346 14.8738 11.3045 14.9369 11.1522C15 10.9999 15.0165 10.8324 14.9843 10.6707C14.9522 10.5091 14.8728 10.3606 14.7562 10.244C14.6397 10.1275 14.4912 10.0481 14.3296 10.016C14.1679 9.98382 14.0004 10.0003 13.8481 10.0634C13.6958 10.1265 13.5657 10.2333 13.4741 10.3703C13.3825 10.5074 13.3337 10.6685 13.3337 10.8333C13.3337 11.0543 13.4215 11.2663 13.5777 11.4225C13.734 11.5788 13.946 11.6666 14.167 11.6666ZM10.0003 15C10.1651 15 10.3263 14.9511 10.4633 14.8595C10.6003 14.768 10.7072 14.6378 10.7702 14.4855C10.8333 14.3333 10.8498 14.1657 10.8176 14.0041C10.7855 13.8424 10.7061 13.6939 10.5896 13.5774C10.473 13.4608 10.3246 13.3815 10.1629 13.3493C10.0013 13.3172 9.83369 13.3337 9.68142 13.3967C9.52915 13.4598 9.399 13.5666 9.30743 13.7037C9.21587 13.8407 9.16699 14.0018 9.16699 14.1666C9.16699 14.3876 9.25479 14.5996 9.41107 14.7559C9.56735 14.9122 9.77931 15 10.0003 15ZM14.167 15C14.3318 15 14.4929 14.9511 14.63 14.8595C14.767 14.768 14.8738 14.6378 14.9369 14.4855C15 14.3333 15.0165 14.1657 14.9843 14.0041C14.9522 13.8424 14.8728 13.6939 14.7562 13.5774C14.6397 13.4608 14.4912 13.3815 14.3296 13.3493C14.1679 13.3172 14.0004 13.3337 13.8481 13.3967C13.6958 13.4598 13.5657 13.5666 13.4741 13.7037C13.3825 13.8407 13.3337 14.0018 13.3337 14.1666C13.3337 14.3876 13.4215 14.5996 13.5777 14.7559C13.734 14.9122 13.946 15 14.167 15ZM5.83366 11.6666C5.99848 11.6666 6.15959 11.6178 6.29663 11.5262C6.43368 11.4346 6.54049 11.3045 6.60356 11.1522C6.66663 10.9999 6.68313 10.8324 6.65098 10.6707C6.61883 10.5091 6.53946 10.3606 6.42291 10.244C6.30637 10.1275 6.15789 10.0481 5.99623 10.016C5.83458 9.98382 5.66703 10.0003 5.51476 10.0634C5.36248 10.1265 5.23234 10.2333 5.14077 10.3703C5.0492 10.5074 5.00033 10.6685 5.00033 10.8333C5.00033 11.0543 5.08812 11.2663 5.2444 11.4225C5.40068 11.5788 5.61265 11.6666 5.83366 11.6666ZM15.8337 3.33329H15.0003V2.49996C15.0003 2.27895 14.9125 2.06698 14.7562 1.9107C14.6 1.75442 14.388 1.66663 14.167 1.66663C13.946 1.66663 13.734 1.75442 13.5777 1.9107C13.4215 2.06698 13.3337 2.27895 13.3337 2.49996V3.33329H6.66699V2.49996C6.66699 2.27895 6.5792 2.06698 6.42291 1.9107C6.26663 1.75442 6.05467 1.66663 5.83366 1.66663C5.61265 1.66663 5.40068 1.75442 5.2444 1.9107C5.08812 2.06698 5.00033 2.27895 5.00033 2.49996V3.33329H4.16699C3.50395 3.33329 2.86807 3.59668 2.39923 4.06553C1.93038 4.53437 1.66699 5.17025 1.66699 5.83329V15.8333C1.66699 16.4963 1.93038 17.1322 2.39923 17.6011C2.86807 18.0699 3.50395 18.3333 4.16699 18.3333H15.8337C16.4967 18.3333 17.1326 18.0699 17.6014 17.6011C18.0703 17.1322 18.3337 16.4963 18.3337 15.8333V5.83329C18.3337 5.17025 18.0703 4.53437 17.6014 4.06553C17.1326 3.59668 16.4967 3.33329 15.8337 3.33329ZM16.667 15.8333C16.667 16.0543 16.5792 16.2663 16.4229 16.4225C16.2666 16.5788 16.0547 16.6666 15.8337 16.6666H4.16699C3.94598 16.6666 3.73402 16.5788 3.57774 16.4225C3.42146 16.2663 3.33366 16.0543 3.33366 15.8333V8.33329H16.667V15.8333ZM16.667 6.66663H3.33366V5.83329C3.33366 5.61228 3.42146 5.40032 3.57774 5.24404C3.73402 5.08776 3.94598 4.99996 4.16699 4.99996H15.8337C16.0547 4.99996 16.2666 5.08776 16.4229 5.24404C16.5792 5.40032 16.667 5.61228 16.667 5.83329V6.66663ZM5.83366 15C5.99848 15 6.15959 14.9511 6.29663 14.8595C6.43368 14.768 6.54049 14.6378 6.60356 14.4855C6.66663 14.3333 6.68313 14.1657 6.65098 14.0041C6.61883 13.8424 6.53946 13.6939 6.42291 13.5774C6.30637 13.4608 6.15789 13.3815 5.99623 13.3493C5.83458 13.3172 5.66703 13.3337 5.51476 13.3967C5.36248 13.4598 5.23234 13.5666 5.14077 13.7037C5.0492 13.8407 5.00033 14.0018 5.00033 14.1666C5.00033 14.3876 5.08812 14.5996 5.2444 14.7559C5.40068 14.9122 5.61265 15 5.83366 15Z" fill="white"/>
                                </svg>
                                </div>
                                <span>일정</span>
                            </label>
                            <input id="schedule_attach" type="text" placeholder="날짜 입력"/>
                        </li>
                    </ul>
                </div>
            )
        }

        return (
            <div className="chat_write_msg">
                <AttachList/>
                {showReservationTime && <ReserveTimeInputPopup/>}
                <div className="con">    
                    <textarea cols={5} rows={4} placeholder="메시지를 입력해주세요." className="test_t" value={message} onChange={e =>setMessage(e.target.value)}/>
                    <div className="attachBox" style={{height:'40px'}}> 
                        <div style={{marginRight:"20px"}}>
                            <img src={Add} alt="Add attach" className="attach" onClick={handleShowAttach}/>
                        </div>
                        <div className="image_attach" style={{display:'flex', gap:'10px'}}>
                            {
                                contents.map((content, index) => {
                                    return <img key={index} src={content} alt="image" style={{width:'44px', height:'44px'}}
                                    onClick={() => {removeImage(index)}}
                                    onDragStart={(e) => e.preventDefault()}/>
                                })
                            }
                        </div>
                    </div>
                </div>
                <button type="button" className="send_btn" onClick={sendMessage}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <g clipPath="url(#clip0_434_5691)">
                        <path d="M19.3961 8.99294L6.52272 14.3681C6.03529 14.5711 5.61625 14.9096 5.3152 15.3434C5.01415 15.7772 4.8437 16.2882 4.82402 16.8159C4.80435 17.3435 4.93628 17.8658 5.2042 18.3208C5.47212 18.7758 5.8648 19.1445 6.33575 19.3833L10.5011 21.5321L12.6378 25.7162C12.8764 26.1909 13.2469 26.5867 13.7048 26.8563C14.1627 27.1258 14.6885 27.2576 15.2194 27.2359C15.903 27.209 16.5523 26.9294 17.0417 26.4513C17.2982 26.1936 17.5013 25.8878 17.6394 25.5515L23.0038 12.6276C23.2111 12.1226 23.2642 11.5676 23.1565 11.0325C23.0487 10.4974 22.7849 10.0063 22.3983 9.62095C22.0116 9.23564 21.5196 8.97346 20.9841 8.86749C20.4487 8.76152 19.8939 8.81651 19.3896 9.02552L19.3961 8.99294ZM15.9448 24.8119C15.8776 24.9743 15.7653 25.1141 15.6212 25.2147C15.4771 25.3153 15.3072 25.3727 15.1316 25.3799C14.9635 25.384 14.7975 25.342 14.6515 25.2584C14.5056 25.1748 14.3853 25.0529 14.3037 24.9058L12.3108 21.021L21.2379 12.0639L15.9448 24.8119ZM11.006 19.7206L7.12113 17.7863C6.97378 17.7053 6.85145 17.5854 6.76739 17.4397C6.68333 17.294 6.64077 17.1281 6.6443 16.96C6.64109 16.8344 6.66359 16.7095 6.71044 16.5929C6.75728 16.4764 6.82748 16.3706 6.9167 16.2822C7.00403 16.1949 7.10801 16.1261 7.22247 16.0798L19.9331 10.7635L11.006 19.7206Z" fill="white"/>
                        </g>
                        <defs>
                        <clipPath id="clip0_434_5691">
                            <rect width="22.1053" height="22.1053" fill="white" transform="translate(0.68457 19.7585) rotate(-58.4478)"/>
                        </clipPath>
                        </defs>
                    </svg>
                </button>
            </div> 
        )
    }
    return (
        <div className="chat">
            <h2>채팅</h2>
            <div className="chatting_box">
                <MessageLog/>
                <WriteMsgContainer/>
            </div>
        </div>
    )
}

export default Chatting